import { useState } from "react";
import { useTranslation } from "react-i18next";
import CommonStyles from "@components/CommonStyles";
import CommonImages from "@base/assets/images";
import { ISite } from "@base/modules/site/site.interface";
import { foldVietnamese } from "@base/modules/doctor/doctorProfile";

interface IProps {
  open: boolean;
  toggle: () => void;
  sites: ISite[];
  /** The clinic chosen last time, shown as selected. */
  selectedCode?: string;
  onPicked: (site: ISite) => void;
}

/** An insurance package may be honoured at several partner clinics; the patient picks one. */
export default function LinkedSitePicker({ open, toggle, sites, selectedCode, onPicked }: IProps) {
  //! state
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState<string>("");

  //! render
  const needle = foldVietnamese(keyword.trim());
  const visibleSites = needle ? sites.filter((site) => foldVietnamese(`${site.name ?? ""} ${site.address ?? ""}`).includes(needle)) : sites;

  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      hiddenAction
      title={t("packageBooking.chooseSite")}
      content={
        <div className="flex flex-col gap-3">
          <CommonStyles.InputUI
            variant="filled"
            allowClear
            value={keyword}
            placeholder={t("packageBooking.searchSite")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setKeyword(event.target.value)}
          />

          <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto">
            {visibleSites.map((site) => (
              <div
                key={site.code}
                onClick={() => onPicked(site)}
                className={`p-3 rounded-lg border cursor-pointer hover:border-common-primary flex items-center gap-3 ${
                  site.code === selectedCode ? "border-common-primary bg-common-primary50" : "border-common-divider bg-common-white"
                }`}
              >
                <img className="w-10 h-10 rounded-lg object-cover shrink-0" src={site.avatar?.[0]?.link || CommonImages.Facilities} alt="" />

                <div className="min-w-0">
                  <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-sm font-medium font-['Inter'] !mb-0">
                    {site.name}
                  </CommonStyles.TypographyUI>
                  <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-xs font-normal font-['Inter'] !mb-0">
                    {site.address}
                  </CommonStyles.TypographyUI>
                </div>
              </div>
            ))}

            {!visibleSites.length ? (
              <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-normal font-['Inter']">
                {t("noResult")}
              </div>
            ) : null}
          </div>
        </div>
      }
    />
  );
}
